import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

const BASE_URL = 'https://yarrowpass.com';

const LOCALE_META: Record<string, { lang: string; ogLocale: string; baseHref: string }> = {
  en: { lang: 'en', ogLocale: 'en_US', baseHref: '/' },
  th: { lang: 'th', ogLocale: 'th_TH', baseHref: '/th/' },
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private doc = inject(DOCUMENT);
  private meta = inject(Meta);
  private localeId = inject(LOCALE_ID) as string;

  private get localeMeta() {
    return LOCALE_META[this.localeId] ?? LOCALE_META['en'];
  }

  /** Call this on every NavigationEnd with the resolved URL (e.g. "/" or "/organizer") */
  updateForRoute(path: string): void {
    this.setHtmlLang();
    this.updateCanonical(path);
    this.updateHreflang(path);
    this.updateOgLocale();
  }

  private setHtmlLang(): void {
    this.doc.documentElement.lang = this.localeMeta.lang;
  }

  private updateCanonical(path: string): void {
    const cleanPath = path.replace(/^\//, '');
    const canonical = `${BASE_URL}${this.localeMeta.baseHref}${cleanPath}`.replace(
      /([^:]\/)\/+/g,
      '$1',
    );

    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
    this.meta.updateTag({ property: 'og:url', content: canonical });
  }

  private updateHreflang(path: string): void {
    // Remove any existing hreflang links to avoid duplication
    this.doc
      .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());

    const cleanPath = path.replace(/^\//, '');

    for (const [, meta] of Object.entries(LOCALE_META)) {
      const href = `${BASE_URL}${meta.baseHref}${cleanPath}`.replace(/([^:]\/)\/+/g, '$1');
      this.appendLink({ rel: 'alternate', hreflang: meta.lang, href });
    }

    // x-default always points to the English (default) version
    const xDefault = `${BASE_URL}/${cleanPath}`.replace(/([^:]\/)\/+/g, '$1');
    this.appendLink({ rel: 'alternate', hreflang: 'x-default', href: xDefault });
  }

  private updateOgLocale(): void {
    this.meta.updateTag({ property: 'og:locale', content: this.localeMeta.ogLocale });

    // og:locale:alternate lists the other locales
    this.meta.removeTag('property="og:locale:alternate"');
    for (const [locale, meta] of Object.entries(LOCALE_META)) {
      if (locale !== this.localeId) {
        this.meta.addTag({ property: 'og:locale:alternate', content: meta.ogLocale });
      }
    }
  }

  private appendLink(attrs: Record<string, string>): void {
    const link = this.doc.createElement('link');
    for (const [key, value] of Object.entries(attrs)) {
      link.setAttribute(key, value);
    }
    this.doc.head.appendChild(link);
  }
}
